import { cva, type VariantProps } from 'class-variance-authority';
import { ReactNode } from 'react';
import { ButtonHTMLAttributes, forwardRef, useEffect, useState } from 'react';
import DynamicIcon, { DynamicIconProps } from 'src/shared/components/ui/icons/dynamic-icon';
import { Loading } from 'src/shared/components/ui/loaders/loading';
import { cn } from 'src/shared/lib/utils';

const buttonVariants = cva(
  'inline-flex cursor-pointer shrink-0 items-center justify-center p-0 rounded-sm focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        light: 'hover:bg-neutral-100 text-typography-grey hover:text-typography-black',
        dark: 'hover:bg-neutral-200 text-typography-grey hover:text-typography-black',
        success: 'hover:bg-neutral-100 text-success',
        destructive: 'hover:bg-neutral-100 hover:text-destructive',
        primary: 'bg-primary hover:bg-primary/80 text-white',
        primaryGhost: 'hover:bg-neutral-100 hover:text-primary',
        slate: 'hover:bg-slate-200 hover:text-typography-black',
        outline: 'hover:bg-neutral-100 border bg-white',
      },
      size: {
        sm: 'h-6 w-6',
        md: 'h-8 w-8',
        lg: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'light',
    },
  }
);

const iconSize = {
  sm: 12,
  md: 16,
  lg: 18,
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  icon: DynamicIconProps['name'];
  stroke?: string;
  size?: 'md' | 'lg' | 'sm';
  loading?: boolean;
  className?: string;
  children?: ReactNode;
}

const IconButton = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ icon, children, size = 'md', variant, stroke, className, loading, disabled, ...props }, ref) => {
    const [throttledLoading, setThrottledLoading] = useState(loading);

    // throttle the loading to avoid flickering when the requests instantly resolves
    useEffect(() => {
      if (loading) {
        setThrottledLoading(true);
        setTimeout(() => setThrottledLoading(false), 500);
      }
    }, [loading]);

    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled || loading || throttledLoading}
        {...props}
      >
        {loading || throttledLoading ? (
          <Loading />
        ) : (
          <DynamicIcon size={iconSize[size]} name={icon} className={cn('shrink-0', stroke)} />
        )}
        {children}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';

export default IconButton;
